// src/pages/user/BookingHistory.jsx
import React, { useContext, useState, useEffect, useRef } from "react";
import { Row, Col, Form } from "react-bootstrap";
import {
  FiSearch,
  FiX,
  FiHash,
  FiCalendar,
  FiUser,
  FiMapPin,
  FiDollarSign,
  FiTool,
  FiAlertCircle,
  FiCheckCircle,
  FiSend,
} from "react-icons/fi";
import {
  FaStar,
  FaShoppingCart,
  FaCheckCircle,
  FaClock,
  FaTools,
  FaTicketAlt,
} from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import AppPagination from "../../../components/general/pagination/Pagination.js";
import UserMasterLayout from "../../../layout/useradmin/UserMasterLayout";
import "../../../assets/css/useradmin/bookinghistory/BookingHistory.css";
import AppContext from "../../../context/AppContext.js";
import axios from "axios";
import Swal from "sweetalert2";
import CancelBookingModal from "../../../components/user/general/stripepages/cancelbooking/Cancelbooking.js";

const EQUIPMENT_CATEGORIES = [
  "Equipment Damage",
  "Service Delay",
  "Payment Issue",
  "Quality Issue",
  "Late Return",
  "Other",
];

const MECHANIC_CATEGORIES = [
  "Poor Repair Quality",
  "Service Not Completed",
  "Overcharging",
  "Late Arrival",
  "Unprofessional Behavior",
  "Other",
];

const EQUIPMENT_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "inprogress", label: "In Progress" },
  { key: "completed", label: "Completed" },
];

const BookingHistory = () => {
  const { baseUrl, loggedInCustomer, baseUrlForFile } = useContext(AppContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState("equipment");
  const [activeStatusTab, setActiveStatusTab] = useState("all");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [equipmentBookings, setEquipmentBookings] = useState([]);
  const [mechanicsBookings, setMechanicsBookings] = useState([]);

  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");

  // ✅ Track which bookings have disputes raised — key = bookingId
  const [disputedBookings, setDisputedBookings] = useState({});

  // ── Cancel Booking state ──
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  const viewCustomerEquipmentsBooking = () => {
    if (!loggedInCustomer) return;
    const token = localStorage.getItem("authToken");
    axios
      .post(
        `${baseUrl}/customer/bookinghistory/viewCustomerEquipmentsBooking`,
        { srcustomer_id: loggedInCustomer.id },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        console.log(res.data);
        if (res.data.success) setEquipmentBookings(res.data.booking);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const viewCustomerMechanicsBooking = () => {
    if (!loggedInCustomer) return;
    const token = localStorage.getItem("authToken");
    axios
      .post(
        `${baseUrl}/customer/bookinghistory/viewCustomerMechanicsBooking`,
        {
          srcustomer_id: loggedInCustomer.id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        console.log(res.data);
        if (res.data.success == true) setMechanicsBookings(res.data.booking);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (loggedInCustomer) {
      viewCustomerEquipmentsBooking();
      viewCustomerMechanicsBooking();
    }
  }, [loggedInCustomer]);

  // ──────────────────────────────────────────────────────────────
  // Resolve the "against" name depending on dispute type
  // (equipment -> rental admin name from DB, mechanic -> mechanic name from DB)
  // ──────────────────────────────────────────────────────────────
  const getAgainstName = (booking) => {
    if (booking.type === "equipment") {
      return (
        booking.srrentaladmin?.fullname ||
        booking.rentaladminname ||
        booking.rentername ||
        `Admin #${booking.srrentaladmin_id}`
      );
    }
    return (
      booking.srmechanicadmin?.fullname ||
      booking.mechanicname ||
      booking.mechanicName ||
      booking.fullname ||
      `Mechanic #${booking.srmechanicadmin_id}`
    );
  };

  // ──────────────────────────────────────────────────────────────
  // riseDispute — submits the dispute ticket matching exact DB columns:
  // id, srcustomer_id, srrentaladmin_id, srmechanicadmin_id,
  // srmaintenancerequest_id, srrentalerequest_id, raisedby, raisedto,
  // category, description, date, status
  //
  // Equipment dispute -> srrentaladmin_id + srrentalerequest_id set
  // Mechanic dispute   -> srmechanicadmin_id + srmaintenancerequest_id set
  // raisedby  = customer's name
  // raisedto  = rental admin's name (equipment) or mechanic's name (mechanic)
  // ──────────────────────────────────────────────────────────────
  const riseDispute = () => {
    const raisedTo = getAgainstName(selectedBooking);

    const formData = new FormData();
    formData.append("srcustomer_id", loggedInCustomer.id);
    formData.append("category", category);
    formData.append("description", description);
    formData.append("date", new Date().toISOString().split("T")[0]);
    formData.append("raisedby", loggedInCustomer?.fullname || "");
    formData.append("raisedto", raisedTo);

    if (selectedBooking.type === "equipment") {
      formData.append("srrentaladmin_id", selectedBooking.srrentaladmin_id);
      formData.append("srrentalerequest_id", selectedBooking.id);
    } else {
      formData.append("srmechanicadmin_id", selectedBooking.srmechanicadmin_id);
      formData.append("srmaintenancerequest_id", selectedBooking.id);
    }

    const token = localStorage.getItem("authToken");
    axios
      .post(`${baseUrl}/customer/dispute/riseDispute`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        console.log(res.data);
        if (res.data.success == false) {
          Swal.fire({
            icon: "error",
            title: "Oops!",
            text:
              res.data.message || "Failed to raise dispute. Please try again.",
          });
          return;
        }

        Swal.fire({
          icon: "success",
          title: "Dispute Raised!",
          text: "Your dispute has been submitted successfully. Our team will get back to you soon.",
          confirmButtonColor: "#FDB714",
        });

        // ✅ Mark this booking as disputed so the button hides in dropdown
        setDisputedBookings((prev) => ({
          ...prev,
          [selectedBooking.id]: true,
        }));

        setShowDisputeModal(false);
        setCategory("");
        setDescription("");
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text: "Something went wrong while raising the dispute. Please try again.",
        });
      });
  };

  const submitEquipmentReview = () => {
    const formData = new FormData();
    formData.append("id", selectedBooking.id);
    formData.append("rating", rating);
    formData.append("review", reviewText);

    const token = localStorage.getItem("authToken");
    axios
      .post(
        `${baseUrl}/customer/bookinghistory/submitEquipmentReview`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Review Submitted!",
            text: "Thank you for your feedback.",
            confirmButtonColor: "#FDB714",
          });
          setShowReviewModal(false);
          setRating(0);
          setReviewText("");
        } else {
          Swal.fire({
            icon: "error",
            title: "Oops!",
            text: res.data.message || "Failed to submit review.",
          });
        }
      });
  };

  const submitMechanicReview = () => {
    const formData = new FormData();
    formData.append("id", selectedBooking.id);
    formData.append("rating", rating);
    formData.append("review", reviewText);

    const token = localStorage.getItem("authToken");
    axios
      .post(
        `${baseUrl}/customer/bookinghistory/submitMechanicReview`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Review Submitted!",
            text: "Thank you for your feedback.",
            confirmButtonColor: "#FDB714",
          });
          setShowReviewModal(false);
          setRating(0);
          setReviewText("");
        } else {
          Swal.fire({
            icon: "error",
            title: "Oops!",
            text: res.data.message || "Failed to submit review.",
          });
        }
      });
  };

  const allBookings =
    activeMainTab === "equipment" ? equipmentBookings : mechanicsBookings;

  const statsData = [
    {
      icon: <FaShoppingCart />,
      value: allBookings.length,
      label: "Total Bookings",
    },
    {
      icon: <FiAlertCircle />,
      value: allBookings.filter(
        (b) => (b.status || "").toLowerCase().trim() === "pending",
      ).length,
      label: "Pending",
    },
    {
      icon: <FaClock />,
      value: allBookings.filter(
        (b) => (b.status || "").toLowerCase().trim() === "inprogress",
      ).length,
      label: "Active Bookings",
    },
    {
      icon: <FaCheckCircle />,
      value: allBookings.filter(
        (b) => (b.status || "").toLowerCase().trim() === "completed",
      ).length,
      label: "Completed",
    },
  ];

  const statusCounts = EQUIPMENT_TABS.reduce((acc, tab) => {
    acc[tab.key] =
      tab.key === "all"
        ? allBookings.length
        : allBookings.filter(
            (b) =>
              (b.status || "").toLowerCase().trim() ===
              tab.key.toLowerCase().trim(),
          ).length;
    return acc;
  }, {});

  const filtered = allBookings.filter((b) => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      activeMainTab === "equipment"
        ? (b.rentername || b.equipmentname || "").toLowerCase().includes(q) ||
          String(b.id || b.bookingId)
            .toLowerCase()
            .includes(q)
        : (b.mechanicName || "").toLowerCase().includes(q) ||
          (b.machineryType || "").toLowerCase().includes(q) ||
          String(b.id).toLowerCase().includes(q);
    const matchStatus =
      activeStatusTab === "all" ||
      (b.status || "").toLowerCase().trim() ===
        activeStatusTab.toLowerCase().trim();

    return matchSearch && matchStatus;
  });

  const itemsPerPage = 5;
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleMainTab = (tab) => {
    setActiveMainTab(tab);
    setActiveStatusTab("all");
    setCurrentPage(1);
    setSearchQuery("");
  };
  const handleStatusTab = (key) => {
    setActiveStatusTab(key);
    setCurrentPage(1);
  };

  const handleView = (b) => {
    setSelectedBooking({ ...b, type: activeMainTab });
    setShowModal(true);
    setActiveDropdown(null);
  };

  const handleDispute = (b) => {
    const booking = { ...b, type: activeMainTab };
    setSelectedBooking(booking);
    setCategory("");
    setDescription("");
    setShowDisputeModal(true);
    setActiveDropdown(null);
  };

  const handleReview = (b) => {
    setSelectedBooking({ ...b, type: activeMainTab });
    setRating(0);
    setReviewText("");
    setShowReviewModal(true);
    setActiveDropdown(null);
  };

  const handleCancelClick = (b, type = "equipment") => {
    setBookingToCancel({ ...b, type });
    setShowCancelModal(true);
    setActiveDropdown(null);
  };

  const confirmCancelBooking = () => {
    if (!bookingToCancel) return;

    const token = localStorage.getItem("authToken");

    const isMechanic = bookingToCancel.type === "mechanics";

    const endpoint = isMechanic
      ? `${baseUrl}/customer/mechanicbooking/cancelMechanicBooking`
      : `${baseUrl}/customer/booking/cancelBooking`;

    return axios
      .post(
        endpoint,
        {
          booking_id: bookingToCancel.id,
          srcustomer_id: loggedInCustomer.id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        setShowCancelModal(false);

        if (!res.data.success) {
          Swal.fire({
            icon: "error",
            title: "Could not cancel",
            text: res.data.message || "Please try again.",
          });
          return;
        }

        Swal.fire({
          icon: "success",
          title: "Booking Cancelled",
          text: res.data.message,
          confirmButtonColor: "#FDB714",
        });

        // Refresh list so the new "cancelled" status shows up
        viewCustomerEquipmentsBooking();
        if (isMechanic) viewCustomerMechanicsBooking();
      })
      .catch((err) => {
        console.log(err);
        setShowCancelModal(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong while cancelling the booking.",
        });
      });
  };

  const handleDisputeSubmit = () => {
    if (!category || !description) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill all fields before submitting.",
      });
      return;
    }

    riseDispute();
  };

  const handleReviewSubmit = () => {
    if (rating === 0) {
      Swal.fire({
        icon: "warning",
        title: "Rating Required",
        text: "Please select a rating before submitting.",
      });
      return;
    }
    if (!reviewText.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Review Required",
        text: "Please write your review before submitting.",
      });
      return;
    }

    if (selectedBooking.type === "equipment") {
      submitEquipmentReview();
    } else {
      submitMechanicReview();
    }
  };

  const getStatusClass = (s) => {
    if (s === "confirmed") return "bh-badge-confirmed";
    if (s === "active") return "bh-badge-active";
    if (s === "completed") return "bh-badge-completed";
    if (s === "cancelled") return "bh-badge-cancelled";
    return "bh-badge-confirmed";
  };

  const getPaymentClass = (s) =>
    s === "paid" ? "bh-badge-paid" : "bh-badge-pending";

  const getModalStatusClass = (s) => {
    if (s === "confirmed") return "bh-modal-badge-confirmed";
    if (s === "active") return "bh-modal-badge-active";
    if (s === "completed") return "bh-modal-badge-completed";
    if (s === "cancelled") return "bh-modal-badge-cancelled";
    return "bh-modal-badge-confirmed";
  };

  React.useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".bh-dropdown-wrapper")) setActiveDropdown(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <UserMasterLayout>
      <div className="bh-page">
        <div className="bh-page-header">
          <h2 className="bh-page-title">Booking History</h2>
          <p className="bh-page-subtitle">
            View all your past and current bookings
          </p>
        </div>

        <Row className="g-3 mb-4">
          {statsData.map((s, i) => (
            <Col key={i} xl={3} lg={3} md={6} sm={12}>
              <div className="bh-stat-card">
                <div className="bh-stat-icon">{s.icon}</div>
                <div className="bh-stat-content">
                  <h3>{s.value}</h3>
                  <p>{s.label}</p>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="bh-main-tabs-wrap">
          <div className="bh-main-tabs">
            {[
              { key: "equipment", label: "Equipment Bookings" },
              { key: "mechanics", label: "Mechanic Bookings" },
            ].map((t) => (
              <button
                key={t.key}
                className={`bh-main-tab ${activeMainTab === t.key ? "bh-main-tab-active" : ""}`}
                onClick={() => handleMainTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bh-table-card">
          <div className="bh-table-header">
            <h3 className="bh-table-title">
              {activeMainTab === "equipment"
                ? "Equipment Bookings"
                : "Mechanic Bookings"}
            </h3>
            <div className="bh-search-box">
              <FiSearch className="bh-search-icon" />
              <input
                type="text"
                className="bh-search-input"
                placeholder={
                  activeMainTab === "equipment"
                    ? "Search by ID or equipment..."
                    : "Search by ID, mechanic or machinery..."
                }
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              {searchQuery && (
                <button
                  className="bh-search-clear"
                  onClick={() => setSearchQuery("")}
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="bh-status-tabs">
            {EQUIPMENT_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`bh-status-tab ${activeStatusTab === tab.key ? "bh-status-tab-active" : ""}`}
                onClick={() => handleStatusTab(tab.key)}
              >
                {tab.label}
                <span className="bh-tab-count">({statusCounts[tab.key]})</span>
              </button>
            ))}
          </div>

          <div className="bh-table-wrapper">
            {activeMainTab === "equipment" ? (
              <table className="bh-table">
                <thead>
                  <tr>
                    <th>BOOKING ID</th>
                    <th>Rental Admin</th>
                    <th>Equipment</th>
                    <th>TOTAL PRICE</th>
                    <th>STATUS</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length > 0 ? (
                    paginated.map((b) => (
                      <tr key={b.id}>
                        <td>{b.id}</td>
                        <td>
                          <div className="bh-seller-cell">
                            <img
                              src={
                                b.srrentaladmin?.image
                                  ? `${baseUrlForFile}/${b.srrentaladmin.image}`
                                  : "/images/default-avatar.png"
                              }
                              alt={b.srrentaladmin?.fullname}
                              className="bh-seller-avatar"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <div className="bh-seller-info">
                              <div className="bh-seller-name">
                                {b.srrentaladmin?.fullname}
                              </div>
                              <div className="bh-seller-email">
                                {b.srrentaladmin?.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{b.srequipmentmanagment.equipmentname}</td>
                        <td>Rs {Number(b.totalprice || 0).toLocaleString()}</td>
                        <td>
                          <span
                            className={`bh-status-badge ${getStatusClass(b.status)}`}
                          >
                            {b.status}
                          </span>
                        </td>
                        <td className="bh-col-action">
                          <div className="bh-dropdown-wrapper">
                            <button
                              className="bh-dots-btn"
                              onClick={() =>
                                setActiveDropdown(
                                  activeDropdown === b.id ? null : b.id,
                                )
                              }
                            >
                              <BsThreeDotsVertical size={16} />
                            </button>
                            {activeDropdown === b.id && (
                              <div className="bh-dropdown-menu">
                                <button
                                  className="bh-dropdown-item"
                                  onClick={() => handleView(b)}
                                >
                                  👁 View Details
                                </button>
                                {(b.status === "pending" ||
                                  b.status === "inprogress") && (
                                  <button
                                    className="bh-dropdown-item"
                                    style={{ color: "#e8590c" }}
                                    onClick={() => handleCancelClick(b)}
                                  >
                                    ✖ Cancel Booking
                                  </button>
                                )}
                                {!disputedBookings[b.id] && (
                                  <button
                                    className="bh-dropdown-item"
                                    onClick={() =>
                                      handleDispute({ ...b, type: "equipment" })
                                    }
                                  >
                                    ⚠️ Raise Dispute
                                  </button>
                                )}
                                {disputedBookings[b.id] && (
                                  <span
                                    className="bh-dropdown-item"
                                    style={{
                                      color: "#aaa",
                                      cursor: "default",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ✅ Dispute Raised
                                  </span>
                                )}
                                {b.status === "completed" && (
                                  <button
                                    className="bh-dropdown-item"
                                    onClick={() =>
                                      handleReview({ ...b, type: "equipment" })
                                    }
                                  >
                                    ⭐ Write Review
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="bh-empty-row">
                        No bookings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="bh-table">
                <thead>
                  <tr>
                    <th>BOOKING ID</th>
                    <th>Mecahnic Admin</th>
                    <th>MACHINERY TYPE</th>
                    <th>PREFERRED DATE</th>
                    <th>BOOKING FEE</th>
                    <th>STATUS</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length > 0 ? (
                    paginated.map((b) => (
                      <tr key={b.id}>
                        <td>{b.id}</td>
                        <td>
                          <div className="bh-seller-cell">
                            <img
                              src={
                                b.srmechanicadmin?.image
                                  ? `${baseUrlForFile}/${b.srmechanicadmin.image}`
                                  : "/images/default-avatar.png"
                              }
                              alt={b.srmechanicadmin?.fullname}
                              className="bh-seller-avatar"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <div className="bh-seller-info">
                              <div className="bh-seller-name">
                                {b.srmechanicadmin?.fullname}
                              </div>
                              <div className="bh-seller-email">
                                {b.srmechanicadmin?.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{b.machinerytype}</td>
                        <td>{b.preferreddate}</td>
                        <td>{b.bookingfee}</td>
                        <td>
                          <span
                            className={`bh-status-badge ${getStatusClass(b.status)}`}
                          >
                            {b.status}
                          </span>
                        </td>
                        <td className="bh-col-action">
                          <div className="bh-dropdown-wrapper">
                            <button
                              className="bh-dots-btn"
                              onClick={() =>
                                setActiveDropdown(
                                  activeDropdown === b.id ? null : b.id,
                                )
                              }
                            >
                              <BsThreeDotsVertical size={16} />
                            </button>
                            {activeDropdown === b.id && (
                              <div className="bh-dropdown-menu">
                                <button
                                  className="bh-dropdown-item"
                                  onClick={() => handleView(b)}
                                >
                                  👁 View Details
                                </button>
                                {(b.status === "pending" ||
                                  b.status === "accepted" ||
                                  b.status === "in_progress") && (
                                  <button
                                    className="bh-dropdown-item"
                                    style={{ color: "#e8590c" }}
                                    onClick={() =>
                                      handleCancelClick(b, "mechanics")
                                    }
                                  >
                                    ✖ Cancel Booking
                                  </button>
                                )}
                                {!disputedBookings[b.id] && (
                                  <button
                                    className="bh-dropdown-item"
                                    onClick={() => handleDispute(b)}
                                  >
                                    ⚠️ Raise Dispute
                                  </button>
                                )}
                                {disputedBookings[b.id] && (
                                  <span
                                    className="bh-dropdown-item"
                                    style={{
                                      color: "#aaa",
                                      cursor: "default",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ✅ Dispute Raised
                                  </span>
                                )}
                                {b.status === "completed" && (
                                  <button
                                    className="bh-dropdown-item"
                                    onClick={() =>
                                      handleReview({ ...b, type: "mechanics" })
                                    }
                                  >
                                    ⭐ Write Review
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="bh-empty-row">
                        No bookings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          <AppPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>

        {/* ══ VIEW DETAIL MODAL ══ */}
        {showModal && selectedBooking && (
          <div className="bh-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="bh-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="bh-modal-header-dark">
                <div>
                  <h5 className="bh-modal-header-title">
                    {selectedBooking.type === "equipment"
                      ? "Equipment Booking Details"
                      : "Mechanic Booking Details"}
                  </h5>
                  <span className="bh-modal-header-sub">
                    Full breakdown of this booking
                  </span>
                </div>
                <button
                  className="bh-modal-close-circle"
                  onClick={() => setShowModal(false)}
                >
                  <FiX />
                </button>
              </div>
              <div className="bh-modal-body">
                <div className="bh-modal-status-bar">
                  <div className="bh-sb-left">
                    <FiHash size={15} color="#FDB714" />
                    <span className="bh-sb-id">{selectedBooking.id}</span>
                    <span
                      className={`bh-modal-badge ${getModalStatusClass(selectedBooking.bookingStatus)}`}
                    >
                      <span className="bh-modal-badge-dot"></span>
                      {selectedBooking.status}
                    </span>
                  </div>
                  <div className="bh-sb-date">
                    <FiCalendar size={13} />
                    {selectedBooking.bookingdate}
                  </div>
                </div>
                {selectedBooking.type === "equipment" ? (
                  <>
                    <p className="bh-section-label">
                      Equipment Booking Information
                    </p>
                    <div className="bh-info-grid">
                      <div className="bh-info-item">
                        <label>Booking ID</label>
                        <span>{selectedBooking.id}</span>
                      </div>
                      <div className="bh-info-item">
                        <label>Customer Name</label>
                        <span>{selectedBooking.srcustomer?.fullname}</span>
                      </div>
                      <div className="bh-info-item">
                        <label>RentalAdmin Name</label>
                        <span>{selectedBooking.srrentaladmin?.fullname}</span>
                      </div>

                      {/* ✅ NEW — Shop Name (from business details via equipment accessor) */}
                      <div className="bh-info-item">
                        <label>Shop Name</label>
                        <span>
                          {selectedBooking.srequipmentmanagment?.shop_name || "—"}
                        </span>
                      </div>

                      {/* ✅ NEW — Shop Address */}
                      <div className="bh-info-item">
                        <label>Shop Address</label>
                        <span>
                          {selectedBooking.srequipmentmanagment?.shop_address || "—"}
                        </span>
                      </div>

                      <div className="bh-info-item">
                        <label>Equipment</label>
                        <span>
                          {selectedBooking.srequipmentmanagment?.equipmentname}
                        </span>
                      </div>
                      <div className="bh-info-item">
                        <label>Duration</label>
                        <span>{selectedBooking.duration} Hour(s)</span>
                      </div>
                      <div className="bh-info-item">
                        <label>Total Price</label>
                        <span>
                          Rs{" "}
                          {Number(
                            selectedBooking.totalprice || 0,
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="bh-info-item">
                        <label>Booking Date</label>
                        <span>{selectedBooking.bookingdate}</span>
                      </div>
                      <div className="bh-info-item">
                        <label>Status</label>
                        <span>{selectedBooking.status}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="bh-section-label">
                      Mechanic Booking Information
                    </p>
                    <div className="bh-info-grid">
                      <div className="bh-info-item">
                        <label>Booking ID</label>
                        <span>{selectedBooking.id}</span>
                      </div>
                      <div className="bh-info-item">
                        <label>Customer Name</label>
                        <span>{selectedBooking.srcustomer?.fullname}</span>
                      </div>
                      <div className="bh-info-item">
                        <label>Mechanic Name</label>
                        <span>{selectedBooking.srmechanicadmin?.fullname}</span>
                      </div>
                      <div className="bh-info-item">
                        <label>Machinery Type</label>
                        <span>{selectedBooking.machinerytype}</span>
                      </div>
                      <div className="bh-info-item">
                        <label>Preferred Date</label>
                        <span>{selectedBooking.preferreddate}</span>
                      </div>
                      <div className="bh-info-item">
                        <label>Location</label>
                        <span>{selectedBooking.location}</span>
                      </div>
                      {/* Booking Fee */}
                      <div className="detail-item">
                        <span className="detail-label">BOOKING FEE</span>
                        <p className="detail-value">
                          Rs. {selectedBooking.bookingfee}
                        </p>
                      </div>

                      {/* Payment */}
                      <div className="detail-item">
                        <span className="detail-label">PAYMENT</span>
                        <p className="detail-value">
                          {selectedBooking.payment || "Pending"}
                        </p>
                      </div>
                      <div className="bh-info-item">
                        <label>Status</label>
                        <span>{selectedBooking.status}</span>
                      </div>
                    </div>
                    <p className="bh-section-label">
                      <FiAlertCircle size={14} /> Issue Description
                    </p>
                    <div className="bh-location-box">
                      <span>{selectedBooking.descriptionissue}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="bh-modal-footer-list">
                <button
                  className="bh-mf-btn bh-mf-close"
                  onClick={() => setShowModal(false)}
                >
                  <FiX size={15} /> Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ RAISE DISPUTE MODAL ══ */}
        {showDisputeModal && selectedBooking && (
          <div
            className="bh-modal-overlay"
            onClick={() => setShowDisputeModal(false)}
          >
            <div
              className="bh-modal-box"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "520px" }}
            >
              <div className="bh-modal-header-dark">
                <div>
                  <h5 className="bh-modal-header-title">Raise a Dispute</h5>
                  <span className="bh-modal-header-sub">
                    Submit a dispute for booking #{selectedBooking.id}
                  </span>
                </div>
                <button
                  className="bh-modal-close-circle"
                  onClick={() => setShowDisputeModal(false)}
                >
                  <FiX />
                </button>
              </div>

              <div className="bh-modal-body">
                {/* Booking Info */}
                <div className="bh-modal-status-bar">
                  <div className="bh-sb-left">
                    <FiHash size={15} color="#FDB714" />
                    <span className="bh-sb-id">{selectedBooking.id}</span>
                    <span className="bh-modal-badge bh-modal-badge-confirmed">
                      <span className="bh-modal-badge-dot"></span>New Dispute
                    </span>
                  </div>
                  <div className="bh-sb-date">
                    <FiCalendar size={13} />
                    {new Date().toLocaleDateString()}
                  </div>
                </div>

                <p className="bh-section-label">
                  <FiUser size={14} /> Dispute Information
                </p>
                <div className="bh-info-grid">
                  <div className="bh-info-item">
                    <label>Raised By</label>
                    <span>
                      {loggedInCustomer?.fullname} ({loggedInCustomer?.id})
                    </span>
                  </div>
                  <div className="bh-info-item">
                    <label>Against</label>
                    <span>{getAgainstName(selectedBooking)}</span>
                  </div>
                  <div className="bh-info-item">
                    <label>Booking ID</label>
                    <span className="bh-info-id">{selectedBooking.id}</span>
                  </div>
                  <div className="bh-info-item">
                    <label>
                      {selectedBooking.type === "equipment"
                        ? "Equipment"
                        : "Machinery"}
                    </label>
                    <span>
                      {selectedBooking.type === "equipment"
                        ? selectedBooking.equipmentname
                        : selectedBooking.machinerytype}
                    </span>
                  </div>
                </div>

                <p className="bh-section-label">
                  <FiAlertCircle size={14} /> Dispute Details
                </p>

                {/* Category */}
                <div className="bh-info-item" style={{ marginBottom: "16px" }}>
                  <label className="bh-form-label">
                    Category * &nbsp;
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#FDB714",
                        fontWeight: 600,
                      }}
                    >
                      (
                      {selectedBooking.type === "equipment"
                        ? "Equipment Rental"
                        : "Mechanic Service"}
                      )
                    </span>
                  </label>
                  <select
                    className="bh-form-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">Select Category</option>
                    {(selectedBooking.type === "equipment"
                      ? EQUIPMENT_CATEGORIES
                      : MECHANIC_CATEGORIES
                    ).map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div className="bh-info-item" style={{ marginBottom: "16px" }}>
                  <label className="bh-form-label">Description *</label>
                  <textarea
                    className="bh-form-textarea"
                    rows={4}
                    placeholder={
                      selectedBooking.type === "equipment"
                        ? "Describe the equipment issue in detail..."
                        : "Describe the mechanic service issue in detail..."
                    }
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>

              <div className="bh-modal-footer-list">
                <button
                  className="bh-mf-btn bh-mf-resolve"
                  onClick={handleDisputeSubmit}
                >
                  <FaTicketAlt size={13} /> Submit Dispute
                </button>
                <button
                  className="bh-mf-btn bh-mf-close"
                  onClick={() => setShowDisputeModal(false)}
                >
                  <FiX size={15} /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ WRITE REVIEW MODAL ══ */}
        {showReviewModal && selectedBooking && (
          <div
            className="bh-modal-overlay"
            onClick={() => setShowReviewModal(false)}
          >
            <div className="bh-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="bh-modal-header-dark">
                <div>
                  <h5 className="bh-modal-header-title">Write a Review</h5>
                  <span className="bh-modal-header-sub">
                    Share your experience
                  </span>
                </div>
                <button
                  className="bh-modal-close-circle"
                  onClick={() => setShowReviewModal(false)}
                >
                  <FiX />
                </button>
              </div>
              <div className="bh-modal-body">
                <div className="bh-info-item" style={{ marginBottom: "16px" }}>
                  <label className="bh-form-label">
                    {selectedBooking.type === "equipment"
                      ? "Equipment"
                      : "Mechanic"}
                  </label>
                  <input
                    className="bh-form-input"
                    value={
                      selectedBooking.type === "equipment"
                        ? selectedBooking.srequipmentmanagment?.equipmentname ||
                          selectedBooking.equipmentname ||
                          `Equipment #${selectedBooking.srequipmentmanagment_id}`
                        : selectedBooking.srmechanicadmin?.fullname ||
                          selectedBooking.mechanicname ||
                          selectedBooking.fullname ||
                          `Mechanic #${selectedBooking.srmechanicadmin_id}`
                    }
                    readOnly
                  />
                </div>
                <p className="bh-section-label">
                  <FaStar size={14} color="#FDB714" /> Your Rating
                </p>
                <div className="bh-star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`bh-star ${rating >= star ? "bh-star-filled" : ""}`}
                      onClick={() => setRating(star)}
                    >
                      ★
                    </span>
                  ))}
                  <span className="bh-rating-text">
                    {rating > 0 ? `${rating} out of 5` : "Select rating"}
                  </span>
                </div>
                <div className="bh-info-item" style={{ marginTop: "16px" }}>
                  <label className="bh-form-label">Your Review *</label>
                  <textarea
                    className="bh-form-textarea"
                    rows={4}
                    placeholder="Share your experience..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                  />
                </div>
              </div>
              <div className="bh-modal-footer-list">
                <button
                  className="bh-mf-btn bh-mf-resolve"
                  onClick={handleReviewSubmit}
                >
                  <FiCheckCircle size={15} /> Submit Review
                </button>
                <button
                  className="bh-mf-btn bh-mf-close"
                  onClick={() => setShowReviewModal(false)}
                >
                  <FiX size={15} /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ══ CANCEL BOOKING CONFIRMATION MODAL ══ */}
        <CancelBookingModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          onConfirm={confirmCancelBooking}
          booking={
            bookingToCancel
              ? {
                  id: bookingToCancel.id,
                  equipmentName:
                    bookingToCancel.type === "mechanics"
                      ? bookingToCancel.machinerytype
                      : bookingToCancel.srequipmentmanagment?.equipmentname,
                  date:
                    bookingToCancel.type === "mechanics"
                      ? bookingToCancel.preferreddate
                      : bookingToCancel.bookingdate,
                }
              : null
          }
        />
      </div>
    </UserMasterLayout>
  );
};

export default BookingHistory;